import { createServerFn } from "@tanstack/react-start";

// US employment (BLS, seasonally adjusted): nonfarm payroll change, unemployment
// rate and average hourly earnings, with gold monthly return in the release
// month. Public data, short cache, last good response retained on outages.

export type NfpMonth = {
  period: string; // YYYY-MM reference month
  payrollsK: number | null; // m/m change in thousands
  unemployment: number | null; // percent
  wagesMoM: number | null; // percent
  wagesYoY: number | null; // percent
  goldReleaseMonthPct: number | null;
};

export type NfpDTO = { months: NfpMonth[]; error?: string; stale?: boolean };

const BLS_URL = "https://api.bls.gov/publicAPI/v2/timeseries/data/";
const PAYROLLS = "CES0000000001";
const UNRATE = "LNS14000000";
const AHE = "CES0500000003";
const TTL_MS = 10 * 60 * 1000;
let cache: { at: number; data: NfpDTO } | null = null;

type Datum = { year: string; period: string; value: string };
const r1 = (n: number) => Math.round(n * 10) / 10;
const pct = (a: number, b: number) => r1(((a - b) / b) * 100);

export const getUsEmployment = createServerFn({ method: "GET" }).handler(async (): Promise<NfpDTO> => {
  if (cache && Date.now() - cache.at < TTL_MS) return cache.data;
  try {
    const { blsFetch } = await import("@/lib/bls-fetch.server");
    const json = await blsFetch([PAYROLLS, UNRATE, AHE], 6, 2);

    const toMap = (id: string) => {
      const m = new Map<string, number>();
      for (const d of json.Results?.series?.find((s) => s.seriesID === id)?.data ?? []) {
        if (!/^M(0[1-9]|1[0-2])$/.test(d.period)) continue;
        const v = Number(d.value);
        if (Number.isFinite(v)) m.set(`${d.year}-${d.period.slice(1)}`, v);
      }
      return m;
    };
    const p = toMap(PAYROLLS);
    const u = toMap(UNRATE);
    const w = toMap(AHE);
    if (p.size < 13) throw new Error("Insufficient payroll history");

    const shift = (per: string, by: number) => {
      const [y, m] = per.split("-").map(Number);
      const d = new Date(Date.UTC(y, m - 1 + by, 1));
      return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
    };

    const gold = new Map<string, number>();
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data } = await supabaseAdmin.from("gold_monthly_history").select("year,month,open,close").limit(1000);
      for (const r of data ?? []) {
        if (r.open > 0) gold.set(`${r.year}-${String(r.month).padStart(2, "0")}`, pct(r.close, r.open));
      }
    } catch (e) {
      console.error("[nfp] gold history unavailable", e);
    }

    const months: NfpMonth[] = [...p.keys()]
      .sort()
      .map((per) => {
        const prev = p.get(shift(per, -1));
        const cur = p.get(per)!;
        const wc = w.get(per);
        const wm = w.get(shift(per, -1));
        const wy = w.get(shift(per, -12));
        return {
          period: per,
          payrollsK: prev != null ? Math.round(cur - prev) : null,
          unemployment: u.get(per) ?? null,
          wagesMoM: wc != null && wm ? pct(wc, wm) : null,
          wagesYoY: wc != null && wy ? pct(wc, wy) : null,
          goldReleaseMonthPct: gold.get(shift(per, 1)) ?? null,
        };
      })
      .filter((m) => m.payrollsK != null);

    const data: NfpDTO = { months };
    cache = { at: Date.now(), data };
    const { saveSnapshot } = await import("@/lib/macro-snapshot.server");
    await saveSnapshot("nfp", data);
    return data;
  } catch (err) {
    const { isBlsGated } = await import("@/lib/bls-fetch.server");
    const gated = isBlsGated(err);
    if (!gated) console.error("[nfp] fetch failed", err);
    if (cache) {
      if (gated) {
        cache = { at: Date.now(), data: cache.data };
        return cache.data;
      }
      return { ...cache.data, stale: true };
    }
    const { loadSnapshot } = await import("@/lib/macro-snapshot.server");
    const snap = await loadSnapshot<NfpDTO>("nfp");
    if (snap?.months?.length) {
      cache = { at: gated ? Date.now() : Date.now() - TTL_MS + 10 * 60 * 1000, data: snap };
      return gated ? snap : { ...snap, stale: true };
    }
    return { months: [], error: "Employment data is temporarily unavailable." };
  }
});
